import { useCallback, useEffect } from "react";
import { IconBrandTelegram } from "@tabler/icons-react";
import {
  PromptInput,
  PromptInputBody,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputTools,
  PromptInputButton,
  PromptInputHeader,
  PromptInputMessage,
  useOptionalPromptInputController,
  toast,
} from "@altimateai/lego";
import { useChatContext } from "../../ChatProvider";
import { CancelGenerationButton } from "../cancelGenerationButton";
import { useEntityDetection } from "../../hooks/useEntityDetection";
import { DetectedEntity, EntityType, ContextOption } from "../../types";
import { SelectedContexts } from "../selectedContexts/SelectedContexts";
import { useAgentChat } from "../../hooks/useAgentChat";
import { updateContext } from "../../store/chatSlice";

interface QuestionFormProps {
  isLoading: boolean;
  allowedFiles?: string;
  placeholder?: string;
  autoFocus?: boolean;
  components?: {
    questionFormLeftActions?: React.ReactNode;
    questionFormButtons?: (userInput: string, onAccept: (text: string) => void) => React.ReactNode;
  };
  initialMessage?: string;
  contextOptions?: ContextOption[];
  /** Per-command helper text shown under the input while the typed message
   *  starts with that command (e.g. {"/opportunity-create": "Describe…"}). */
  commandHints?: Record<string, string>;
}

export const QuestionForm: React.FC<QuestionFormProps> = ({
  isLoading,
  allowedFiles,
  placeholder = "Ask anything, take any action...",
  autoFocus = false,
  initialMessage: _initialMessage = "",
  components: componentsFromProps,
  contextOptions,
  commandHints,
}) => {
  const { currentSessionId, sessions, dispatch } = useChatContext();
  const { sendMessage } = useAgentChat();
  const { detectEntities } = useEntityDetection();
  const controller = useOptionalPromptInputController();

  const userInput = controller?.textInput.value || "";
  const currentContext = currentSessionId ? sessions[currentSessionId]?.context : {};

  // Check for unknown entities
  const entities =
    (currentSessionId && (sessions[currentSessionId]?.context?.entities as DetectedEntity[])) || [];
  const unknownEntities = entities.filter(entity => entity.selected_type === EntityType.UNKNOWN);
  const hasUnknownEntities = unknownEntities.length > 0;
  const canSubmitForm = !hasUnknownEntities;

  const handleSubmit = async (message: PromptInputMessage, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!message.text.trim() || isLoading || !canSubmitForm) return;

    if (!currentSessionId) return;

    try {
      controller?.textInput.setInput("");
      await sendMessage({
        message: message.text,
        files: message.files,
      });
    } catch (e) {
      console.log(e);
    }
  };

  // Trigger entity detection when user types
  useEffect(() => {
    if (userInput.trim().length > 2) {
      detectEntities(userInput.trim());
    }
  }, [userInput, detectEntities]);

  // Handler to remove context
  const handleRemoveContext = (id: string, key?: string) => {
    if (!currentSessionId || !key) return;

    const currentOptions = (currentContext?.[key] as string[]) || [];
    const updatedOptions = currentOptions.filter(optionId => optionId !== id);

    dispatch(
      updateContext({
        sessionId: currentSessionId,
        context: { [key]: updatedOptions },
      })
    );
  };

  const trimmedInput = userInput.trimStart();
  const activeCommandHint = Object.entries(commandHints || {}).find(([command]) =>
    trimmedInput.startsWith(command)
  )?.[1];
  const handleFileError = useCallback(
    (error: { code: string }) => {
      if (error.code === "accept" && allowedFiles) {
        toast({
          title: "Unsupported file type",
          description: `Supported files: ${allowedFiles.split(",").join(", ")}`,
          variant: "destructive",
        });
      }
    },
    [allowedFiles]
  );

  return (
    <div className="al-w-full ">
      <PromptInput
        globalDrop
        multiple
        accept={allowedFiles}
        onError={handleFileError}
        onSubmit={handleSubmit}
        className="al-shadow-lg al-rounded-2xl al-border al-border-gray-300 al-bg-white  [&_[data-slot='input-group']]:!al-rounded-2xl [&_[data-slot='input-group']]:!al-ring-0 [&_[data-slot='input-group']]:!al-ring-offset-0 [&_*]:focus:!al-outline-none [&_*]:focus:!al-ring-0 [&_*]:focus:!al-ring-offset-0"
      >
        <PromptInputHeader align={"block-start"}>
          <SelectedContexts contextOptions={contextOptions} onRemoveContext={handleRemoveContext} />
        </PromptInputHeader>
        <PromptInputBody className="al-w-full">
          {/* Main textarea */}
          <PromptInputTextarea
            placeholder={placeholder}
            autoFocus={autoFocus}
            disabled={isLoading}
            className="al-px-4 al-pt-0 al-overflow-y-auto al-resize-none placeholder:al-italic placeholder:al-font-normal placeholder:al-text-sm placeholder:al-leading-5 placeholder:al-text-[#7A899E] focus:al-outline-none"
            aria-label="Question input"
            aria-required="true"
            aria-invalid={false}
          />
        </PromptInputBody>

        {/* Helper text for the active slash command, so the user knows what
            to type after it. */}
        {activeCommandHint && (
          <div className="al-px-4 al-pb-1 al-text-xs al-italic al-text-[#7A899E]">
            {activeCommandHint}
          </div>
        )}

        {/* Bottom footer with all actions */}
        <PromptInputFooter className="al-w-full al-px-3 al-pb-2 al-pt-2">
          <div className="al-flex al-items-center al-justify-between al-w-full al-gap-2">
            {/* Left side: Left actions */}
            <div className="al-flex al-items-center al-gap-2 al-flex-1 al-min-w-0">
              {/* Left actions slot (e.g., "+ Add Datamate") */}
              {componentsFromProps?.questionFormLeftActions && (
                <div className="al-flex al-items-center al-gap-2 al-flex-shrink-0">
                  {componentsFromProps.questionFormLeftActions}
                </div>
              )}
            </div>

            {/* Right side: Question form buttons + settings + submit/cancel (FIXED, never wraps) */}
            <PromptInputTools className="al-flex-shrink-0">
              {/* Question form custom buttons (e.g., ImprovePromptButton with sparkles icon) */}
              {componentsFromProps?.questionFormButtons?.(userInput, (improvedPrompt: string) => {
                controller?.textInput.setInput(improvedPrompt);
              })}

              {/* Cancel or Send button */}
              {isLoading ? (
                <CancelGenerationButton />
              ) : (
                <PromptInputButton
                  type="submit"
                  disabled={isLoading || !userInput.trim() || !canSubmitForm}
                  className="!al-w-10 !al-h-10 al-min-h-6 al-rounded-md al-bg-[#247EFE] hover:al-bg-[#1e6ad9] disabled:al-opacity-50 disabled:al-cursor-not-allowed al-flex al-items-center al-justify-center al-flex-shrink-0"
                  aria-busy={isLoading}
                  aria-label={
                    isLoading
                      ? "Sending message..."
                      : hasUnknownEntities
                        ? "Cannot send - unknown entities need types selected"
                        : "Send message"
                  }
                  title={
                    isLoading
                      ? "Cancel generation"
                      : hasUnknownEntities
                        ? `Please select right type for ${unknownEntities.map(e => e.name).join(", ")}`
                        : "Send message"
                  }
                >
                  <IconBrandTelegram className="al-h-3 al-w-3 al-text-white" />
                  <span className="al-sr-only">
                    {isLoading ? " message in progress" : " message"}
                  </span>
                </PromptInputButton>
              )}
            </PromptInputTools>
          </div>
        </PromptInputFooter>
      </PromptInput>

      {/* Screen reader hint */}
      <div className="al-sr-only">Press Enter to send, Shift + Enter for new line</div>
    </div>
  );
};
